import { Box, IconButton, SvgIcon, Typography } from '@mui/material';
import { FunctionComponent, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import Icons from '@Constants/Icons.constants';
import EmailFileUpload from '@Containers/Emails/EmailFileUpload';
import { FileUploadResponse } from '@Models/upload.models';
import { setUploadData } from '@Slices/upload.slice';
import { useAppDispatch } from '@Utils/hooks/useStore.hook';

import { ImageBox } from './EmailHeaderFooter.styles';
import { EmailHeaderFooterProps } from './EmailHeaderFooter.types';

const EmailHeaderFooter: FunctionComponent<EmailHeaderFooterProps> = (
  props: EmailHeaderFooterProps
): JSX.Element => {
  const { images, viewOnly, storeKey, ...resProps } = props;

  /** Hooks */
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  /** Removes attachment on deletion */
  const onDelete = () => {
    dispatch(
      setUploadData({
        storeKey,
        attachment: {
          isLoading: false,
          data: [],
        },
      })
    );
  };

  return images?.length || !viewOnly ? (
    <Box
      sx={{
        px: 1.6,
        pt: 0.6,
        pb: 0.8,
        minHeight: '7.4rem',
        display: 'flex',
        alignItems: 'center',
        backgroundColor: ({ colors }) => colors.greyBackground,
        borderRadius: 1,
      }}
    >
      {!images?.length && <EmailFileUpload storeKey={storeKey} {...resProps} />}
      {images?.map(
        (image: FileUploadResponse, index: number): ReactNode => (
          <Box key={index} display="flex" alignItems="center">
            <ImageBox>
              <img src={image.url} alt={t('common.uploads.uploaded_image')} />
            </ImageBox>
            {!viewOnly && (
              <IconButton onClick={onDelete}>
                <SvgIcon>{Icons.close}</SvgIcon>
              </IconButton>
            )}
          </Box>
        )
      )}
      {!viewOnly && (
        <Box ml="auto">
          <Typography
            sx={{ color: 'text.secondary', mb: 0.8 }}
            variant="helpText"
          >
            {t('common.uploads.image_format')}
          </Typography>
          <Typography sx={{ color: 'text.secondary' }} variant="helpText">
            {t('common.uploads.recommended_resolution', {
              resolution: '200x60',
            })}
          </Typography>
        </Box>
      )}
    </Box>
  ) : (
    <></>
  );
};

export default EmailHeaderFooter;
